import clsx from "clsx";
import React, { createContext } from "react";

interface HeaderContextValue {
  subheader: React.ReactNode;
  setSubheader: React.Dispatch<React.SetStateAction<React.ReactNode>>;
}

const HeaderContext = createContext<HeaderContextValue | undefined>(undefined);

export const useHeaderContext = () => {
  const context = React.useContext(HeaderContext);
  if (!context) {
    throw new Error("useHeaderContext must be used within a HeaderContextProvider");
  }
  return [context.subheader, context.setSubheader] as const;
};

interface TemplateProps {
  header: React.ReactNode;
  subheader?: React.ReactNode;
  children: React.ReactNode;
  height?: string;
}

const smallStyle: React.CSSProperties = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: '11.51px',
  fontWeight: 100,
  letterSpacing: '2px',
  lineHeight: '16px',
  textShadow: '1px 1px 3px rgba(82, 82, 82, 0.4)'
};

const bigStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  color: 'rgb(255, 255, 255)',
  display: 'block',
  fontFamily: 'Poppins, sans-serif',
  fontSize: '19.18px',
  fontWeight: 500,
  letterSpacing: '0.25px',
  lineHeight: '15.6px',
  textAlign: 'left',
  textShadow: 'rgba(82, 82, 82, 0.4) 1px 1px 3px',
  textSizeAdjust: '100%',
  unicodeBidi: 'isolate',
  WebkitFontSmoothing: 'antialiased',
  WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
  marginBottom: 6.5
};

const Template = ({ header, subheader: initialSubHeader, height = "64", children }: TemplateProps) => {
  const [subheader, setSubheader] = React.useState<React.ReactNode>(initialSubHeader);

  return (
    <HeaderContext.Provider value={{ subheader, setSubheader }}>
      <div className={clsx('bg-carnation-500 w-full px-8 z-0', { "pb-64": height === "64" }, { "pb-72": height === "72" })}>
        <header className="max-w-screen-xl mx-auto flex flex-col justify-center" style={{ marginBottom: 10, height: 70, minHeight: 70, maxHeight: 70, paddingBottom: "3.25px", paddingTop: 13.25 }}>
          <h1 style={subheader ? smallStyle : bigStyle}>{header}</h1>
          {subheader && <h2 style={bigStyle}>{subheader}</h2>}
        </header>
      </div>
      <div className={clsx('w-full px-8', { '-mt-64': height === "64" }, { '-mt-72': height === "72" })}>
        <main className="max-w-screen-xl mx-auto">
          {children}
        </main>
      </div>
    </HeaderContext.Provider>
  );
};

export default Template;
